/**
 * The compte-rendu detail screen — render/preview, the per-channel diffusion board, and comptoir
 * print, organized in keyboard-navigable tabs. Drives the single read (GET /results/comptes-rendus/
 * :id) and implements all 5 states: offline (with cache awareness), loading (skeleton), error
 * (NOT_FOUND → a tailored empty), empty (no lines handled by the preview), forbidden (route-level +
 * the print tab's own canPrint guard). Remembers the CR in the local per-lab recents on a successful
 * load. The diffusion + print tabs are only meaningful for an `emis` CR.
 */
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button, OfflineState, EmptyState, ErrorState, LoadingState, Tabs } from "@/ui";
import type { TabItem } from "@/ui";
import { useT, useUiStore } from "@/app";
import { ERROR_CODES } from "@/api";
import { PageHeader } from "./components/PageHeader";
import { EtatBadge, TypeBadge } from "./components/StatusBadges";
import { CompteRenduPreview } from "./components/CompteRenduPreview";
import { DiffusionBoard } from "./components/DiffusionBoard";
import { ComptoirPrint } from "./components/ComptoirPrint";
import { useCompteRendu } from "./hooks";
import { useRecentComptesRendus } from "./recent";

type TabId = "preview" | "diffusion" | "print";

export function CompteRenduPage() {
  const { t } = useT();
  const navigate = useNavigate();
  const params = useParams<{ id: string }>();
  const id = params.id ?? null;
  const online = useUiStore((s) => s.online);

  const { data, isLoading, isError, error, refetch, labId } = useCompteRendu(id);
  const { remember } = useRecentComptesRendus(labId);
  const [tab, setTab] = useState<TabId>("preview");

  // remember this CR in the local recents once it loads (non-clinical handle only).
  useEffect(() => {
    if (data) {
      remember({ id: data.id, numero: data.numero, version: data.version, etat: data.etat });
    }
  }, [data, remember]);

  const back = (
    <Button variant="ghost" leadingIcon="‹" onClick={() => navigate("/resultats")}>
      {t("results.emission.back")}
    </Button>
  );

  // ── 5-state DoD ──────────────────────────────────────────────────────────────
  if (!online && !data) {
    return <OfflineState onRetry={() => void refetch()} cached={false} />;
  }
  if (isLoading) {
    return <LoadingState variant="table" />;
  }
  if (isError) {
    // NOT_FOUND is a "this id doesn't exist" empty rather than a system error.
    if (error?.code === ERROR_CODES.NOT_FOUND || error?.httpStatus === 404) {
      return (
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-4)" }}>
          {back}
          <EmptyState glyph="⌀" title={t("results.notFound")} description={t("results.notFoundDesc")} />
        </div>
      );
    }
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-4)" }}>
        {back}
        <ErrorState
          message={error?.message ?? t("results.emission.error")}
          {...(error?.code ? { code: error.code } : {})}
          onRetry={() => void refetch()}
        />
      </div>
    );
  }
  if (!data) {
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-4)" }}>
        {back}
        <EmptyState glyph="⌀" title={t("results.notFound")} description={t("results.notFoundDesc")} />
      </div>
    );
  }

  const cr = data;
  const isEmitted = cr.etat === "emis";

  const tabs: ReadonlyArray<TabItem> = [
    {
      id: "preview",
      label: t("results.tab.preview"),
      content: <CompteRenduPreview cr={cr} />,
    },
    {
      id: "diffusion",
      label: t("results.tab.diffusion"),
      disabled: !isEmitted,
      content: isEmitted ? (
        <DiffusionBoard compteRenduId={cr.id} canaux={cr.canaux} />
      ) : (
        <EmptyState glyph="○" title={t("results.diffusion.onlyEmitted")} />
      ),
    },
    {
      id: "print",
      label: t("results.tab.print"),
      disabled: !isEmitted,
      content: <ComptoirPrint cr={cr} active={tab === "print"} />,
    },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--nv-s-5)" }}>
      <PageHeader
        title={
          <span className="nv-ltr-island" style={{ fontVariantNumeric: "tabular-nums" }}>
            {t("results.cr.title", { numero: cr.numero })}
          </span>
        }
        subtitle={
          <span>
            {t("results.meta.demande")}{" "}
            <span className="nv-mono nv-ltr-island">{cr.demande_analyse_id}</span>
          </span>
        }
        meta={
          <span style={{ display: "inline-flex", gap: "var(--nv-s-2)", alignItems: "center" }}>
            <EtatBadge etat={cr.etat} />
            <TypeBadge type={cr.type} />
            <span style={{ color: "var(--nv-slate)", fontSize: "var(--nv-fs-caption)" }}>
              {t("results.version", { n: cr.version })}
            </span>
          </span>
        }
        actions={
          <>
            <Button
              variant="ghost"
              leadingIcon="↻"
              onClick={() => void refetch()}
              disabled={!online}
            >
              {t("results.action.refresh")}
            </Button>
            {back}
          </>
        }
      />

      {!online && (
        <p
          role="status"
          style={{
            margin: 0,
            padding: "var(--nv-s-2) var(--nv-s-3)",
            borderRadius: "var(--nv-r-sm)",
            background: "var(--nv-tint-high)",
            color: "var(--nv-warn)",
            fontSize: "var(--nv-fs-caption)",
          }}
        >
          ⚠ {t("state.offline.title")}
        </p>
      )}

      <Tabs items={tabs} value={tab} onChange={(id) => setTab(id as TabId)} aria-label={t("results.cr.titleFallback")} />
    </div>
  );
}
